import React from "react";
import {
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  Check,
  ChevronDown,
} from "lucide-react";
import "./creatorStudio.css";
import "./step2.css";
import {
  DraftVariations,
  GenerateCampaignResponse,
} from "../../../../types/creator-studio";
import { useSelectVariation } from "../../../../hooks/use-select-variation";

interface Step2Props {
  onBack: () => void;
  generatedCampaign: GenerateCampaignResponse | null;
  onNext: (selectedVariation: DraftVariations) => void;
  initialSelectedVariation?: DraftVariations | null;
  regenerationFeedback: string;
  setRegenerationFeedback: (feedback: string) => void;
  regenerationFeedbackHistory: string[];
  onRegenerateWithFeedback: () => void;
  onClearRegenerationFeedbackHistory: () => void;
  isRegenerating?: boolean;
}

const Step2: React.FC<Step2Props> = ({
  onBack,
  generatedCampaign,
  onNext,
  initialSelectedVariation,
  regenerationFeedback,
  setRegenerationFeedback,
  regenerationFeedbackHistory,
  onRegenerateWithFeedback,
  onClearRegenerationFeedbackHistory,
  isRegenerating = false,
}) => {
  const {
    selectedVariation,
    setSelectedVariation,
    variations: aiVariations,
  } = useSelectVariation(generatedCampaign, initialSelectedVariation);

  const renderInlineText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
      if (!part.match(/^https?:\/\//)) {
        return part;
      }

      return (
        <a
          className="variation-link"
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          key={`${part}-${index}`}
          onClick={(event) => event.stopPropagation()}
        >
          {part}
        </a>
      );
    });
  };

  const renderVariationBody = (body: string) => {
    const sections = body.split(/\n{2,}/).filter((section) => section.trim());

    return sections.map((section, sectionIndex) => {
      const lines = section
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);
      const isBulletSection =
        lines.length > 0 && lines.every((line) => line.startsWith("• "));

      if (isBulletSection) {
        return (
          <ul className="variation-list" key={`list-${sectionIndex}`}>
            {lines.map((line, lineIndex) => (
              <li key={`item-${sectionIndex}-${lineIndex}`}>
                {line.replace(/^•\s*/, "")}
              </li>
            ))}
          </ul>
        );
      }

      return (
        <p className="variation-paragraph" key={`paragraph-${sectionIndex}`}>
          {renderInlineText(section)}
        </p>
      );
    });
  };

  return (
    <div className="creator-studio-wrapper step2-bg">
      <div className="step2-content">
        <div className="step2-header">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft
              size={18}
              style={{ verticalAlign: "middle", marginRight: "6px" }}
            />
            Back to Prompt
          </button>
          <div style={{ flex: 1 }}></div>
          {selectedVariation !== null && (
            <div className="selection-status">
              Variation {String.fromCharCode(65 + selectedVariation)} selected
            </div>
          )}
          <button
            className="continue-btn"
            onClick={() => {
              if (selectedVariation !== null) {
                onNext(aiVariations[selectedVariation]);
              }
            }}
          >
            Continue with Variation{" "}
            {selectedVariation !== null
              ? String.fromCharCode(65 + selectedVariation)
              : ""}{" "}
            <ArrowRight
              size={18}
              style={{ verticalAlign: "middle", marginLeft: "4px" }}
            />
          </button>
        </div>

        <div className="step2-hero">
          <h1>Choose your direction</h1>
          <p>
            Our AI has drafted three distinct approaches based on your prompt.
            Select the one that best fits your brand voice and target audience.
          </p>
        </div>

        <div className="variations-grid">
          {aiVariations.map((variation, index) => (
            <div
              key={variation.id}
              className={`variation-card ${variation.color} ${
                selectedVariation === index ? "selected" : ""
              }`}
              onClick={() => setSelectedVariation(index)}
            >
              <div className="variation-header">
                <span className="variation-label">{variation.label}</span>
                {generatedCampaign?.preferred_variation === index &&
                  selectedVariation !== index && (
                    <span className="preferred-badge">Recommended</span>
                  )}
                {selectedVariation === index && (
                  <div className="check-circle">
                    <Check size={18} strokeWidth={2.5} />
                  </div>
                )}
              </div>
              <div className="variation-body">
                <h3>{variation.subject}</h3>
                <div className="variation-copy">
                  {renderVariationBody(variation.body)}
                </div>
                <div className="read-more">
                  Read More{" "}
                  <ChevronDown size={20} style={{ verticalAlign: "middle" }} />
                </div>
              </div>
              <div className="variation-footer">
                <div className="focus-chip">Focus: {variation.focus}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="step2-footer">
          <div className="regenerate-feedback-panel">
            <div className="regenerate-feedback-copy">
              <label htmlFor="regeneration-feedback">
                Regenerate with feedback
              </label>
              <p>
                Tell Impret what should change. Feedback is remembered until
                you clear it.
              </p>
            </div>
            {regenerationFeedbackHistory.length > 0 && (
              <div className="feedback-memory">
                <div className="feedback-memory-header">
                  <span>Applied feedback</span>
                  <button
                    type="button"
                    onClick={onClearRegenerationFeedbackHistory}
                    disabled={isRegenerating}
                  >
                    Clear
                  </button>
                </div>
                <ol>
                  {regenerationFeedbackHistory.map((feedback, index) => (
                    <li key={`${feedback}-${index}`}>{feedback}</li>
                  ))}
                </ol>
              </div>
            )}
            <textarea
              id="regeneration-feedback"
              className="regenerate-feedback-textarea"
              placeholder="Example: Make it shorter, less promotional, and add a stronger hook."
              value={regenerationFeedback}
              onChange={(event) => setRegenerationFeedback(event.target.value)}
            />
            <button
              className="regenerate-btn"
              onClick={onRegenerateWithFeedback}
              disabled={isRegenerating}
            >
              <span className="icon">
                <RefreshCw size={18} />
              </span>{" "}
              {isRegenerating ? "Regenerating..." : "Regenerate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2;
